import cx from 'classnames';
import { BUYERS_NO_RESULTS, BZ2_MESSAGES_FOR_PRODUCT, BZ2_REMOVED_BUYERS } from 'mk/autogenerated/translations/ProductBuyers.9740053e2cf8cf35ff1d3aa16f3adefd'
import ClosedBuyers from 'mk2/apps/bazaar/components/ClosedBuyers';
import { MessageFormData } from 'mk2/apps/bazaar/components/MessageForm';
import ProductBuyer from 'mk2/apps/bazaar/components/ProductBuyer';
import { ReviewFormData } from 'mk2/apps/bazaar/components/ReviewForm';
import { BazaarMessageEntity, BazaarOrderEntity, BuyerEntity, ReviewEntity } from 'mk2/apps/bazaar/schemas';
import { Btn, BtnType } from 'mk2/components/Btn';
import { LineHeader } from 'mk2/components/LineHeader';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { UserEntity } from 'mk2/schemas';
import React, { useMemo, useState } from 'react';
import styles from './ProductBuyers.mscss';

const emptyArray = [];

interface OwnProps {
    className?: string;
    buyers: BuyerEntity[];
    closedBuyers: BuyerEntity[];
    messages: BazaarMessageEntity[];
    reviews: ReviewEntity[];
    requestUser: UserEntity;
    requestUserPermissions: string[];
    bazaarOrders?: BazaarOrderEntity[];

    onRead(messagesIds: number[]);
    onNewReview(buyerId: number, values: ReviewFormData);
    onNewMessage(buyerId: number, values: MessageFormData);
    onRemoveBuyer(buyerId: number);
    onRestoreBuyer(buyerId: number);
}

type Props = OwnProps;

const ProductBuyers: React.FunctionComponent<Props> = ({
    className,
    buyers,
    closedBuyers,
    messages,
    reviews,
    requestUser,
    requestUserPermissions,
    bazaarOrders,
    onRead,
    onRemoveBuyer,
    onRestoreBuyer,
    onNewMessage,
    onNewReview,
}) => {
    const [showClosedBuyers, setShowClosedBuyers] = useState<boolean>(false);

    const onShowClosedBuyers = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            setShowClosedBuyers(true);
        },
        [setShowClosedBuyers],
    );

    const messagesByBuyerUser = useMemo(() => {
        return messages.reduce((acc, cur) => {
            if (!acc.has(cur.buyer.user.id)) {
                acc.set(cur.buyer.user.id, []);
            }
            acc.get(cur.buyer.user.id).push(cur);
            return acc;
        }, new Map<number, BazaarMessageEntity[]>());
    }, [messages]);

    const reviewsByProductAndBuyerUser = useMemo(() => {
        return reviews.reduce((acc, cur) => {
            if (!cur.product) {
                // Filter unknown products
                return acc;
            }

            const reviewerKey = `${cur.product.id}-${cur.reviewer.id}`;
            const reviewedKey = `${cur.product.id}-${cur.reviewed.id}`;

            if (!acc.has(reviewerKey)) {
                acc.set(reviewerKey, []);
            }
            if (!acc.has(reviewedKey)) {
                acc.set(reviewedKey, []);
            }

            acc.get(reviewerKey).push(cur);
            acc.get(reviewedKey).push(cur);
            return acc;
        }, new Map<string, ReviewEntity[]>());
    }, [reviews]);

    // Sort by last message time
    buyers.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

    return (
        <div className={cx(styles.ProductBuyers, className)}>
            <LineHeader heading={BZ2_MESSAGES_FOR_PRODUCT} />
                <div
                    className={cx(
                        styles.ProductBuyers__buyers,
                        buyers.length === 0 && styles['ProductBuyers__buyers--empty'],
                    )}
                >
                    {buyers.length > 0
                        ? buyers.map((buyer) => (
                            <ProductBuyer
                                className={styles.ProductBuyers__buyer}
                                key={buyer.id}
                                buyer={buyer}
                                messages={messagesByBuyerUser.get(buyer.user.id) || emptyArray}
                                reviews={
                                    reviewsByProductAndBuyerUser.get(`${buyer.product.id}-${buyer.user.id}`) || emptyArray
                                }
                                requestUser={requestUser}
                                requestUserPermissions={requestUserPermissions}
                                bazaarOrders={bazaarOrders}
                                onRead={onRead}
                                onNewReview={onNewReview}
                                onNewMessage={onNewMessage}
                                onRemoveBuyer={onRemoveBuyer}
                              />
                          ))
                        : BUYERS_NO_RESULTS}
                </div>
            {closedBuyers.length > 0 && (
                <div
                    className={cx(
                        styles.ProductBuyers__closedBuyers,
                        !showClosedBuyers && styles['ProductBuyers__closedBuyers--hidden'],
                    )}
                >
                    {!showClosedBuyers ? (
                        <Btn
                            className={styles.ProductBuyers__closedBuyers__button}
                            type={BtnType.BlueOutline}
                            onClick={onShowClosedBuyers}
                            label={
                                <>
                                    {BZ2_REMOVED_BUYERS}{' '}
                                    <span className={styles.ProductBuyers__closedBuyers__button__count}>
                                        ({closedBuyers.length})
                                    </span>
                                </>
                            }
                        />
                    ) : (
                        <ClosedBuyers buyers={closedBuyers} onRestore={onRestoreBuyer} />
                    )}
                </div>
            )}
        </div>
    );
};

ProductBuyers.displayName = 'ProductBuyers';

export default React.memo<Props>(ProductBuyers);
